"use client";

import {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ClientStorage } from "../utils/ClientStorage";

function getCurrentValue<T, K extends string = string>(
  storage: ClientStorage<K>,
  storageKey: K,
  defaultValue?: T
): T | null {
  return (storage.getItem(storageKey) as T | null) ?? defaultValue ?? null;
}

export function usePersistentStateString<
  T extends string = string,
  K extends string = string,
>(
  storage: ClientStorage<K>,
  storageKey: K
): readonly [T | null, (newValue: SetStateAction<T | null>) => void];
export function usePersistentStateString<
  T extends string = string,
  K extends string = string,
>(
  storage: ClientStorage<K>,
  storageKey: K,
  defaultValue: T
): readonly [T, (newValue: SetStateAction<T>) => void];
export function usePersistentStateString<
  T extends string = string,
  K extends string = string,
>(storage: ClientStorage<K>, storageKey: K, defaultValue?: T) {
  const [state, setState] = useState<T | null>(() =>
    getCurrentValue(storage, storageKey, defaultValue)
  );

  useEffect(() => {
    const newValue = getCurrentValue(storage, storageKey, defaultValue);
    setState(newValue);
  }, [defaultValue, storage, storageKey]);

  const setValue = useCallback(
    (action: SetStateAction<T | null>) => {
      const newValue =
        typeof action === "function"
          ? action(getCurrentValue(storage, storageKey, defaultValue))
          : action;
      storage.setItem(storageKey, newValue);
      setState(newValue);
    },
    [defaultValue, storage, storageKey]
  );

  // Listen for changes to localStorage from other tabs
  useEffect(() => {
    const dispose = storage.addListener(storageKey, (newValue) => {
      setState(newValue as T | null);
    });

    return () => dispose();
  }, [storage, storageKey]);

  return useMemo(() => [state, setValue] as const, [setValue, state]);
}

export function usePersistentStateObject<T extends object, K extends string>(
  storage: ClientStorage<K>,
  storageKey: K,
  defaultValue: T
): [T, (newValue: SetStateAction<T>) => void] {
  const [state, setState] = usePersistentStateString<string, K>(
    storage,
    storageKey,
    JSON.stringify(defaultValue)
  );

  const parsedState = useMemo(() => {
    try {
      return JSON.parse(state);
    } catch {
      return defaultValue;
    }
  }, [state, defaultValue]);

  return useMemo(
    () => [
      parsedState,
      (action) => {
        setState((prev) => {
          const parsedPrev = JSON.parse(prev);

          const newValue =
            typeof action === "function" ? action(parsedPrev) : action;

          return JSON.stringify(newValue);
        });
      },
    ],
    [parsedState, setState]
  );
}
